import { PositionSide } from '@kwenta/sdk/types'

import { type ChangeEvent, useCallback, useMemo, useState } from 'react'
import type { ConditionOrderTableItem } from 'types/futures'

import { selectEditCOModalInputs, selectPerpsProvider } from 'state/futures/common/selectors'
import { editIsolatedMarginConditionalOrderPrice } from 'state/futures/isolatedMargin/actions'
import { editCrossMarginConditionalOrderPrice } from 'state/futures/snxPerpsV3/actions'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { providerIsCrossMargin } from 'utils/futures'

import OrderPriceInput from '../OrderPriceInput'

type Props = {
	order: ConditionOrderTableItem
}

export default function EditOrderPriceInput({ order }: Props) {
	const dispatch = useAppDispatch()
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const { orderPrice } = useAppSelector(selectEditCOModalInputs)

	const initialValue = useMemo(() => order.targetPrice.toString(2), [order.targetPrice])
	const [isUserInput, setIsUserInput] = useState(false)
	const positionSide = order?.side ?? (order.size.lt(0) ? PositionSide.SHORT : PositionSide.LONG)

	const onSizeChange = useCallback(
		(value: string) => {
			setIsUserInput(true)
			if (providerIsCrossMargin(perpsProvider)) {
				dispatch(editCrossMarginConditionalOrderPrice(order, value))
			} else {
				dispatch(editIsolatedMarginConditionalOrderPrice(order, value))
			}
		},
		[dispatch, order, perpsProvider]
	)

	const onChangeValue = useCallback(
		(_: ChangeEvent<HTMLInputElement>, v: string) => {
			onSizeChange(v)
		},
		[onSizeChange]
	)

	return (
		<OrderPriceInput
			orderType={order.orderType}
			orderPrice={isUserInput ? orderPrice?.price || '' : initialValue}
			positionSide={positionSide}
			marketPrice={order.currentPrice.price.toString()}
			onChange={onChangeValue}
		/>
	)
}
